// /js/services/bookService.js
import { supabase } from './supabaseClient.js';
import { showToast } from '../modules/toast.js';

const DEFAULT_CURRENCY = 'NGN';
const CURRENCY_FORMATS = {
    NGN: { locale: 'en-NG', currency: 'NGN' },
    USD: { locale: 'en-US', currency: 'USD' },
    GHS: { locale: 'en-GH', currency: 'GHS' },
    EUR: { locale: 'en-IE', currency: 'EUR' },
    GBP: { locale: 'en-GB', currency: 'GBP' }
};
const EXCHANGE_RATES = { NGN: 1, USD: 0.00065, GHS: 0.0095, EUR: 0.0006, GBP: 0.0005 };

class BookService {
    constructor() {
        this.tableName = 'books';
        this.purchasesTable = 'purchases';
        this.downloadTokensTable = 'download_tokens';
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000;
    }

    formatPrice(amount, currency = DEFAULT_CURRENCY) {
        if (!amount) return '₦0';
        const format = CURRENCY_FORMATS[currency];
        if (!format) return `₦${amount}`;
        try {
            return new Intl.NumberFormat(format.locale, {
                style: 'currency',
                currency: format.currency,
                minimumFractionDigits: 0
            }).format(amount);
        } catch (e) {
            return `${currency} ${amount}`;
        }
    }

    // ============================================
    // GET BOOKS WITH PAGINATION AND FILTERS
    // ============================================
    async getBooks(page = 1, limit = 20, filters = {}) {
        try {
            const cacheKey = this.generateCacheKey(page, limit, filters);
            const cached = this.getFromCache(cacheKey);
            if (cached) return cached;

            let query = supabase.from(this.tableName).select('*', { count: 'exact' });

            // Filters
            if (filters.category) query = query.contains('categories', [filters.category]);
            if (filters.author) query = query.eq('author', filters.author);
            if (filters.isFeatured !== undefined) query = query.eq('is_featured', filters.isFeatured);
            if (filters.isActive !== undefined) query = query.eq('is_active', filters.isActive);
            if (filters.isSubscriptionOnly !== undefined) query = query.eq('is_subscription_only', filters.isSubscriptionOnly);
            if (filters.minPrice) query = query.gte('price', filters.minPrice);
            if (filters.maxPrice) query = query.lte('price', filters.maxPrice);

            // Search
            if (filters.search) {
                const searchTerm = `%${filters.search.toLowerCase()}%`;
                query = query.or(`title.ilike.${searchTerm},author.ilike.${searchTerm},description.ilike.${searchTerm}`);
            }

            // Sorting
            const sortMap = {
                'price_low': { column: 'price', ascending: true },
                'price_high': { column: 'price', ascending: false },
                'title_asc': { column: 'title', ascending: true },
                'title_desc': { column: 'title', ascending: false },
                'newest': { column: 'created_at', ascending: false },
                'oldest': { column: 'created_at', ascending: true }
            };
            const sort = sortMap[filters.sortBy] || sortMap['newest'];
            query = query.order(sort.column, { ascending: sort.ascending });

            // Pagination
            const startIndex = (page - 1) * limit;
            query = query.range(startIndex, startIndex + limit - 1);

            const { data, error, count } = await query;

            if (error) throw error;

            const response = {
                success: true,
                data: this.enrichBooks(data || []),
                total: count || 0,
                page: page,
                limit: limit,
                totalPages: Math.ceil((count || 0) / limit)
            };

            this.addToCache(cacheKey, response);
            return response;

        } catch (error) {
            console.error('Error fetching books:', error);
            return { success: false, error: error.message };
        }
    }

    // ============================================
    // GET ALL BOOKS
    // ============================================
    async getAllBooks() {
        try {
            const cacheKey = 'all_books';
            const cached = this.getFromCache(cacheKey);
            if (cached) return cached;

            const { data, error } = await supabase
                .from(this.tableName)
                .select('*')
                .eq('is_active', true)
                .order('created_at', { ascending: false });

            if (error) throw error;

            const response = {
                success: true,
                data: this.enrichBooks(data || [])
            };
            this.addToCache(cacheKey, response);
            return response;

        } catch (error) {
            console.error('Error fetching all books:', error);
            return { success: false, error: error.message };
        }
    }

    // ============================================
    // GET BOOK BY ID
    // ============================================
    async getBook(bookId) {
        try {
            const cacheKey = `book_${bookId}`;
            const cached = this.getFromCache(cacheKey);
            if (cached) return cached;

            const { data, error } = await supabase
                .from(this.tableName)
                .select('*')
                .eq('id', bookId)
                .single();

            if (error) throw error;

            const response = {
                success: true,
                data: this.enrichBook(data)
            };
            this.addToCache(cacheKey, response);
            return response;

        } catch (error) {
            console.error('Error fetching book:', error);
            return { success: false, error: error.message };
        }
    }

    // ============================================
    // ENRICH BOOK
    // ============================================
    enrichBook(book) {
        if (!book) return book;
        const enriched = { ...book };

        // Cover URLs
        const coverUrl = book.cover_url || '/assets/images/placeholder-book.svg';
        enriched.coverUrl = coverUrl;
        enriched.coverOptimizedUrl = coverUrl;
        enriched.coverThumbnailUrl = coverUrl;
        enriched.coverSmallUrl = coverUrl;

        // Format price
        if (enriched.price) {
            const currency = enriched.currency || DEFAULT_CURRENCY;
            enriched.formattedPrice = this.formatPrice(enriched.price, currency);
            enriched.formattedPriceUSD = this.formatPrice(enriched.price * (EXCHANGE_RATES?.USD || 0.00065), 'USD');
            enriched.formattedPriceGHS = this.formatPrice(enriched.price * (EXCHANGE_RATES?.GHS || 0.0095), 'GHS');
        }

        // Map Supabase fields to camelCase for frontend compatibility
        enriched.isFeatured = book.is_featured;
        enriched.isSubscriptionOnly = book.is_subscription_only;
        enriched.isActive = book.is_active;
        enriched.pageCount = book.page_count;
        enriched.downloadCount = book.download_count;
        enriched.viewCount = book.view_count;
        enriched.fileUrl = book.file_url;
        enriched.fileType = book.file_type;
        enriched.filePath = book.file_path;
        enriched.coverPath = book.cover_path;
        enriched.epubUrl = book.epub_url;
        enriched.createdAt = book.created_at;
        enriched.updatedAt = book.updated_at;

        return enriched;
    }

    enrichBooks(books) {
        return (books || []).map(book => this.enrichBook(book));
    }

    // ============================================
    // GET CATEGORIES
    // ============================================
    async getCategories() {
        try {
            const cacheKey = 'categories';
            const cached = this.getFromCache(cacheKey);
            if (cached) return cached;

            const result = await this.getAllBooks();
            if (!result.success) return result;

            const categoryMap = new Map();
            result.data.forEach(book => {
                if (book.categories) {
                    book.categories.forEach(cat => {
                        categoryMap.set(cat, (categoryMap.get(cat) || 0) + 1);
                    });
                }
            });

            const categories = Array.from(categoryMap.entries()).map(([name, count]) => ({
                name,
                count,
                id: name.toLowerCase().replace(/\s+/g, '-')
            }));
            categories.sort((a, b) => b.count - a.count);

            const response = { success: true, data: categories };
            this.addToCache(cacheKey, response);
            return response;

        } catch (error) {
            console.error('Error fetching categories:', error);
            return { success: false, error: error.message };
        }
    }

    // ============================================
    // GET AUTHORS
    // ============================================
    async getAuthors() {
        try {
            const cacheKey = 'authors';
            const cached = this.getFromCache(cacheKey);
            if (cached) return cached;

            const result = await this.getAllBooks();
            if (!result.success) return result;

            const authorMap = new Map();
            result.data.forEach(book => {
                if (book.author) {
                    authorMap.set(book.author, (authorMap.get(book.author) || 0) + 1);
                }
            });

            const authors = Array.from(authorMap.entries()).map(([name, count]) => ({
                name,
                count,
                id: name.toLowerCase().replace(/\s+/g, '-')
            }));
            authors.sort((a, b) => b.count - a.count);

            const response = { success: true, data: authors };
            this.addToCache(cacheKey, response);
            return response;

        } catch (error) {
            console.error('Error fetching authors:', error);
            return { success: false, error: error.message };
        }
    }

    // ============================================
    // ADD BOOK
    // ============================================
    async addBook(bookData) {
        try {
            if (!bookData.title || !bookData.author) {
                throw new Error('Title and author are required');
            }

            const book = {
                title: bookData.title.trim(),
                author: bookData.author.trim(),
                description: bookData.description || '',
                categories: bookData.categories || [],
                price: parseFloat(bookData.price) || 0,
                currency: bookData.currency || DEFAULT_CURRENCY,
                is_featured: bookData.isFeatured || false,
                is_subscription_only: bookData.isSubscriptionOnly || false,
                page_count: parseInt(bookData.pageCount) || 0,
                language: bookData.language || 'en',
                publisher: bookData.publisher || '',
                publication_date: bookData.publicationDate || null,
                cover_url: bookData.coverUrl || null,
                cover_path: bookData.coverPath || null,
                file_url: bookData.fileUrl || null,
                file_path: bookData.filePath || null,
                file_type: bookData.fileType || null,
                epub_url: bookData.epubUrl || null,
                is_active: true,
                download_count: 0,
                view_count: 0
            };

            const { data, error } = await supabase
                .from(this.tableName)
                .insert([book])
                .select()
                .single();

            if (error) throw error;

            this.clearCache();

            return {
                success: true,
                bookId: data.id,
                book: this.enrichBook(data),
                message: 'Book added successfully'
            };

        } catch (error) {
            console.error('Error adding book:', error);
            return { success: false, error: error.message };
        }
    }

    // ============================================
    // UPDATE BOOK
    // ============================================
    async updateBook(bookId, updateData) {
        try {
            const updates = {
                title: updateData.title,
                author: updateData.author,
                description: updateData.description,
                categories: updateData.categories,
                price: parseFloat(updateData.price) || 0,
                currency: updateData.currency,
                is_featured: updateData.isFeatured,
                is_subscription_only: updateData.isSubscriptionOnly,
                is_active: updateData.isActive,
                page_count: parseInt(updateData.pageCount) || 0,
                language: updateData.language,
                publisher: updateData.publisher,
                updated_at: new Date().toISOString()
            };

            // Only include fields that are provided
            if (updateData.coverUrl !== undefined) updates.cover_url = updateData.coverUrl;
            if (updateData.coverPath !== undefined) updates.cover_path = updateData.coverPath;
            if (updateData.fileUrl !== undefined) updates.file_url = updateData.fileUrl;
            if (updateData.filePath !== undefined) updates.file_path = updateData.filePath;
            if (updateData.fileType !== undefined) updates.file_type = updateData.fileType;
            if (updateData.epubUrl !== undefined) updates.epub_url = updateData.epubUrl;

            // Remove undefined values
            Object.keys(updates).forEach(key => {
                if (updates[key] === undefined) delete updates[key];
            });

            const { data, error } = await supabase
                .from(this.tableName)
                .update(updates)
                .eq('id', bookId)
                .select()
                .single();

            if (error) throw error;

            this.clearCache();
            return {
                success: true,
                book: this.enrichBook(data),
                message: 'Book updated successfully'
            };

        } catch (error) {
            console.error('Error updating book:', error);
            return { success: false, error: error.message };
        }
    }

    // ============================================
    // DELETE BOOK
    // ============================================
    async deleteBook(bookId) {
        try {
            const bookResult = await this.getBook(bookId);
            if (!bookResult.success) throw new Error('Book not found');

            const book = bookResult.data;

            // Delete files from Supabase Storage
            if (book.filePath) {
                await supabase.storage.from('books').remove([book.filePath]);
            }
            if (book.coverPath) {
                await supabase.storage.from('covers').remove([book.coverPath]);
            }

            // Delete from database
            const { error } = await supabase
                .from(this.tableName)
                .delete()
                .eq('id', bookId);

            if (error) throw error;

            this.clearCache();
            return { success: true, message: 'Book deleted successfully' };

        } catch (error) {
            console.error('Error deleting book:', error);
            return { success: false, error: error.message };
        }
    }

    // ============================================
    // INCREMENT COUNTERS
    // ============================================
    async incrementViewCount(bookId) {
        try {
            const book = await this.getBook(bookId);
            if (book.success) {
                await supabase
                    .from(this.tableName)
                    .update({ view_count: (book.data.viewCount || 0) + 1 })
                    .eq('id', bookId);
                this.removeFromCache(`book_${bookId}`);
            }
        } catch (error) {
            console.error('Error incrementing view count:', error);
        }
    }

    async incrementDownloadCount(bookId) {
        try {
            const book = await this.getBook(bookId);
            if (book.success) {
                await supabase
                    .from(this.tableName)
                    .update({ download_count: (book.data.downloadCount || 0) + 1 })
                    .eq('id', bookId);
                this.removeFromCache(`book_${bookId}`);
            }
        } catch (error) {
            console.error('Error incrementing download count:', error);
        }
    }

    // ============================================
    // GET RELATED BOOKS
    // ============================================
    async getRelatedBooks(bookId, limit = 4) {
        try {
            const book = await this.getBook(bookId);
            if (!book.success) return { success: false, error: 'Book not found' };

            const categories = book.data.categories || [];
            if (categories.length === 0) return { success: true, data: [] };

            const { data, error } = await supabase
                .from(this.tableName)
                .select('*')
                .contains('categories', [categories[0]])
                .eq('is_active', true)
                .neq('id', bookId)
                .limit(limit);

            if (error) throw error;
            return { success: true, data: this.enrichBooks(data || []) };

        } catch (error) {
            console.error('Error fetching related books:', error);
            return { success: false, error: error.message };
        }
    }

    // ============================================
    // PURCHASE MANAGEMENT
    // ============================================
    async createPurchase(purchaseData) {
        try {
            console.log('📝 createPurchase called with:', purchaseData);

            const purchase = {
                book_id: purchaseData.bookId,
                user_id: purchaseData.userId,
                user_email: purchaseData.userEmail,
                user_name: purchaseData.userName || '',
                amount: purchaseData.amount,
                expected_amount: purchaseData.expectedAmount || purchaseData.amount,
                amount_verified: purchaseData.amountVerified || false,
                currency: purchaseData.currency || DEFAULT_CURRENCY,
                payment_method: purchaseData.paymentMethod || 'paystack',
                payment_reference: purchaseData.paymentReference,
                status: purchaseData.status || 'pending',
                device_id: purchaseData.deviceId || '',
                device_info: purchaseData.deviceInfo || {},
                download_count: 0,
                max_downloads: purchaseData.maxDownloads || 3,
                download_expiry: purchaseData.downloadExpiry || 48
            };

            const { data, error } = await supabase
                .from(this.purchasesTable)
                .insert([purchase])
                .select()
                .single();

            if (error) throw error;

            return {
                success: true,
                purchaseId: data.id,
                purchase: data
            };

        } catch (error) {
            console.error('❌ Error creating purchase:', error);
            return { success: false, error: error.message };
        }
    }

    async hasUserPurchasedBook(userId, bookId) {
        try {
            const { data, error } = await supabase
                .from(this.purchasesTable)
                .select('*')
                .eq('user_id', userId)
                .eq('book_id', bookId)
                .in('status', ['completed', 'active'])
                .limit(1);

            if (error) throw error;

            return {
                success: true,
                hasPurchased: data && data.length > 0,
                purchase: data && data.length > 0 ? data[0] : null
            };

        } catch (error) {
            console.error('Error checking purchase:', error);
            return { success: false, error: error.message };
        }
    }

    async getUserPurchases(userId) {
        try {
            const { data, error } = await supabase
                .from(this.purchasesTable)
                .select('*, books(*)')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            if (error) throw error;

            return { success: true, data: data || [] };

        } catch (error) {
            console.error('Error fetching user purchases:', error);
            return { success: false, error: error.message };
        }
    }

    async getPurchase(purchaseId) {
        try {
            const { data, error } = await supabase
                .from(this.purchasesTable)
                .select('*')
                .eq('id', purchaseId)
                .single();

            if (error) throw error;
            return { success: true, data };

        } catch (error) {
            console.error('Error fetching purchase:', error);
            return { success: false, error: error.message };
        }
    }

    async getUserPurchaseForBook(userId, bookId) {
        try {
            const { data, error } = await supabase
                .from(this.purchasesTable)
                .select('*')
                .eq('user_id', userId)
                .eq('book_id', bookId)
                .in('status', ['completed', 'active'])
                .limit(1);

            if (error) throw error;

            if (data && data.length > 0) {
                return { success: true, purchase: data[0] };
            }
            return { success: false, error: 'Purchase not found' };

        } catch (error) {
            console.error('Error getting user purchase:', error);
            return { success: false, error: error.message };
        }
    }

    async updatePurchaseStatus(purchaseId, status, paymentData = {}) {
        try {
            const { data, error } = await supabase
                .from(this.purchasesTable)
                .update({
                    status: status,
                    ...paymentData,
                    updated_at: new Date().toISOString()
                })
                .eq('id', purchaseId)
                .select()
                .single();

            if (error) throw error;
            return { success: true, message: `Purchase ${status}` };

        } catch (error) {
            console.error('Error updating purchase:', error);
            return { success: false, error: error.message };
        }
    }

    async verifyPurchase(purchaseId) {
        try {
            const { data, error } = await supabase
                .from(this.purchasesTable)
                .update({
                    status: 'completed',
                    updated_at: new Date().toISOString()
                })
                .eq('id', purchaseId)
                .select()
                .single();

            if (error) throw error;
            return { success: true, purchase: data };

        } catch (error) {
            console.error('Error verifying purchase:', error);
            return { success: false, error: error.message };
        }
    }

    // ============================================
    // DOWNLOAD TOKEN MANAGEMENT
    // ============================================
    async generateDownloadToken(purchaseId, userId, bookId) {
        try {
            const token = this.generateSecureToken();
            const expiresAt = new Date();
            expiresAt.setHours(expiresAt.getHours() + 48);

            const { data, error } = await supabase
                .from(this.downloadTokensTable)
                .insert([{
                    purchase_id: purchaseId,
                    user_id: userId,
                    book_id: bookId,
                    token: token,
                    expires_at: expiresAt.toISOString(),
                    used: false
                }])
                .select()
                .single();

            if (error) throw error;

            return {
                success: true,
                token: token,
                tokenId: data.id,
                expiresAt: expiresAt
            };

        } catch (error) {
            console.error('Error generating download token:', error);
            return { success: false, error: error.message };
        }
    }

    async validateDownloadToken(token, deviceId) {
        try {
            const { data, error } = await supabase
                .from(this.downloadTokensTable)
                .select('*')
                .eq('token', token)
                .eq('used', false)
                .single();

            if (error || !data) {
                return { success: false, error: 'Invalid or expired token' };
            }

            const expiresAt = new Date(data.expires_at);
            if (expiresAt < new Date()) {
                return { success: false, error: 'Token has expired' };
            }

            if (data.device_id && data.device_id !== deviceId) {
                return { success: false, error: 'This download is bound to another device' };
            }

            return { success: true, tokenData: data };

        } catch (error) {
            console.error('Error validating token:', error);
            return { success: false, error: error.message };
        }
    }

    async useDownloadToken(tokenId, deviceId) {
        try {
            const { data, error } = await supabase
                .from(this.downloadTokensTable)
                .update({
                    used: true,
                    device_id: deviceId,
                    used_at: new Date().toISOString()
                })
                .eq('id', tokenId)
                .select()
                .single();

            if (error) throw error;
            return { success: true, data };

        } catch (error) {
            console.error('Error using download token:', error);
            return { success: false, error: error.message };
        }
    }

    generateSecureToken() {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let token = '';
        for (let i = 0; i < 32; i++) {
            token += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return token;
    }

    // ============================================
    // CACHE MANAGEMENT
    // ============================================
    generateCacheKey(page, limit, filters) {
        return `books_${page}_${limit}_${JSON.stringify(filters)}`;
    }

    getFromCache(key) {
        if (this.cache.has(key)) {
            const { data, timestamp } = this.cache.get(key);
            if (Date.now() - timestamp < this.cacheTimeout) {
                return data;
            } else {
                this.cache.delete(key);
            }
        }
        return null;
    }

    addToCache(key, data) {
        this.cache.set(key, { data, timestamp: Date.now() });
    }

    removeFromCache(key) {
        this.cache.delete(key);
    }

    clearCache() {
        this.cache.clear();
    }
}

const bookService = new BookService();
export default bookService;